import React, { useEffect, useRef, useState } from "react";
import "./business-details.css";
import { useAuth } from "../auth/auth-context";
import { BusinessDetailsViewModel } from "./business-details.view-model";
import LoadingPlaceHolder from "src/components/molecules/loading/loading-placeholder";
import { IBusinessDetailsModel } from "src/types/models/apiModels";
import InputBox from "src/components/atoms/v2/inputBox/inputBox";

interface BusinessDetailsProps {
	businessDetails?: IBusinessDetailsModel;
	onChange?: (details: IBusinessDetailsModel) => void;
	businessCountryCode: string;
}

const BusinessDetails: React.FC<BusinessDetailsProps> = ({
	businessDetails,
	onChange,
	businessCountryCode,
}) => {
	const [details, setDetails] = useState<IBusinessDetailsModel>(
		businessDetails ?? {
			fields: [],
		}
	);
	const { user } = useAuth(); // Extract user from useAuth hook
	const [loading, setLoading] = useState(true);
	const viewModel = useRef<BusinessDetailsViewModel | null>(null);

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true); // Always set loading to true before fetching
			try {
				if (user && user.accessToken) {
					viewModel.current = new BusinessDetailsViewModel(
						user.accessToken
					);
					const response =
						await viewModel.current?.initBusinessDetails(
							businessCountryCode // Use the updated businessCountryCode prop
						);
					setDetails(() => response);
				}
			} catch (error) {
				console.error("Error fetching initBusiness:", error);
			} finally {
				setLoading(false); // Set loading to false after fetching
			}
		};

		if (!businessDetails) fetchData();
	}, [user, businessCountryCode]); // Add businessCountryCode to the dependency array

	useEffect(() => {
		if (onChange) {
			onChange(details);
		}
	}, [details, onChange]); // Ensure onChange is included in the dependency array

	const handleFieldChange = (field: string, value: string) => {
		setDetails((prevInfo) => ({
			...prevInfo,
			fields: prevInfo.fields.map((f) =>
				f.name === field ? { ...f, value: value } : f
			),
		}));
	};

	if (loading) {
		return <LoadingPlaceHolder message="Get data.." />;
	}

	return (
		<div className="flex flex-col items-center justify-center gap-4 w-full">
			<div className="flex items-center justify-start gap-2 w-full">
				<span className="font-Poppins font-medium text-2xl uppercase tracking-wider">
					Additional Information
				</span>
			</div>

			{details.fields.map((item: any, index: any) => (
				<div
					key={index}
					className="flex items-center justify-center w-full"
				>
					<InputBox
						label={item.title}
						type={item.type}
						value={item.value}
						onChange={(value) =>
							handleFieldChange(item.name, value)
						}
						required
					/>
				</div>
			))}
		</div>
	);
};

export default BusinessDetails;
